//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import '@uia/intelligent-product-tagging/new-product-suggestion-overlay';
import universalImageActionsProductizeOverlayTemplate from '@legacy-components/common/universal-image-actions/universal-image-actions-productize-overlay.html';

(function () {
  const uia = angular.module('uia');
  const SPATIAL_TAG_SIZE = 36;

  uia.directive('universalImageActionsProductizeOverlay', [
    '$timeout',
    '$window',
    'SessionVarsService',
    function ($timeout, $window, SessionVarsService) {
      return {
        restrict: 'E',
        scope: {
          imageUrl: '=',
          features: '=',
        },
        template: universalImageActionsProductizeOverlayTemplate,
        link(scope, element) {
          let clientX;
          let clientY;
          let mouseDownOnContainer = false;
          const $spatialTagContainer = angular.element(
            element[0].querySelectorAll('.spatial-tag-container'),
          );

          const spatialTagContainerOnMouseDown = function (event) {
            mouseDownOnContainer = true;
          };

          const spatialTagContainerOnMouseUp = function (event) {
            if (mouseDownOnContainer) {
              scope.model.beginAddProductFlow(event);
            }
            mouseDownOnContainer = false;
          };

          // Once we've loaded the imageUrl source and we know the dimensions, we'll
          // set the width / height of the spatial-tag-container to match that of the
          // image. This is done so that we properly calculate spatial tag offsets
          // and so that we can limit the spatial tag's valid area to within the image
          // itself.
          $timeout(() => {
            const unbindWatcher = scope.$watch('imageUrl', (newVal, oldVal) => {
              const $imagePlaceholder = angular.element('<img />');
              const $spatialTaggingOverlay = angular.element(
                '.spatial-tagging-overlay',
              );

              if (angular.isDefined(newVal || oldVal)) {
                $imagePlaceholder
                  .appendTo($spatialTaggingOverlay)
                  .css('display', 'none')
                  .attr('src', scope.imageUrl)
                  .load(function () {
                    this.remove();
                    unbindWatcher();
                  });
              }
            });
          });

          $spatialTagContainer.on('mousedown', spatialTagContainerOnMouseDown);
          $spatialTagContainer.on('mouseup', spatialTagContainerOnMouseUp);
          scope.$on('$destroy', () => {
            $spatialTagContainer.off(
              'mousedown',
              spatialTagContainerOnMouseDown,
            );
            $spatialTagContainer.off('mouseup', spatialTagContainerOnMouseUp);
          });
        },
        controller: [
          '$rootScope',
          '$scope',
          'ProductTaggingService',
          'UniversalImageActionsService',
          function (
            $rootScope,
            $scope,
            ProductTaggingService,
            UniversalImageActionsService,
          ) {
            let containerWidth;
            let containerHeight;

            function mouseEventOffsetsToPercentOffsets(offsetX, offsetY) {
              containerWidth =
                containerWidth ||
                angular.element('.spatial-tag-container:not(.ng-hide)').width();
              containerHeight =
                containerHeight ||
                angular
                  .element('.spatial-tag-container:not(.ng-hide)')
                  .height();
              const maxOffsetX = containerWidth - SPATIAL_TAG_SIZE / 2;
              const maxOffsetY = containerHeight - SPATIAL_TAG_SIZE / 2;
              const minOffsetX = SPATIAL_TAG_SIZE / 2;
              const minOffsetY = SPATIAL_TAG_SIZE / 2;
              const correctedOffsetX = Math.min(
                Math.max(offsetX, minOffsetX),
                maxOffsetX,
              );
              const correctedOffsetY = Math.min(
                Math.max(offsetY, minOffsetY),
                maxOffsetY,
              );
              const percentOffsetX = correctedOffsetX / containerWidth;
              const percentOffsetY = correctedOffsetY / containerHeight;
              return {
                percentOffsetX,
                percentOffsetY,
              };
            }

            $scope.model = {
              addingProduct: false,
              editor: {
                enabled: false,
              },
              products: [],
              editorSpatialTag: null,
              position: null,
              isIntelligentProductTaggingEnabled:
                SessionVarsService.isFeatureEnabled(
                  'uia_intelligent_product_tagging',
                ) ||
                SessionVarsService.isFeatureEnabled(
                  'intelligent_product_tagging_v2',
                ),
              universalItemId:
                UniversalImageActionsService.getCurrentLaunchOptions().getUniversalItemId(),
              loading: true,
            };

            const beginAddProductFlowInProductizePanel = function (
              offsetX,
              offsetY,
              boxId,
            ) {
              $rootScope.$broadcast('uia:products:begin-add-product-flow', {
                offsetX,
                offsetY,
                boxId,
              });
            };

            function eventIsWithinSpatialTagContainer($event) {
              return angular
                .element($event.target)
                .hasClass('spatial-tag-container');
            }

            function getNextSpatialTagPosition() {
              return (
                _.chain($scope.model.products)
                  .map((product) => {
                    return product.spatialTag;
                  })
                  .compact()
                  .value().length + 1
              );
            }

            function fadeSpatialTagCTABorder(fadeIn) {
              angular.element('.spatial-tag-cta-container').css({
                opacity: fadeIn ? '1.0' : '0.5',
              });
            }

            function setSpatialTagPosition(offsetX, offsetY, isNewTag) {
              const existingSpatialTag = $scope.model.editorSpatialTag || {};
              const position = parseInt(
                existingSpatialTag.label || getNextSpatialTagPosition(),
                10,
              );
              const spatialTag = {
                x: offsetX,
                y: offsetY,
                label: position.toString(),
              };

              $scope.model.editorSpatialTag = spatialTag;
              $scope.model.position = position;
              if (!isNewTag) {
                // Broadcast this update to the 'universal-image-actions-productize' directive to update
                // the product being edited.
                $scope.model.loading = true;
                $rootScope.$broadcast(
                  'uia:products:updateSpatialTag',
                  position - 1,
                  spatialTag,
                );
              }
              enableSpatialTagDragging();
            }

            function enableSpatialTagDragging() {
              $timeout(() => {
                const offset = { x: 0, y: 0 };
                angular.element('.spatial-tag.draggable').draggable({
                  containment: '.spatial-tag-container',
                  start() {
                    fadeSpatialTagCTABorder(false);
                  },
                  drag(e, ui) {
                    $scope.isDragging = true;
                    offset.x = ui.position.left + SPATIAL_TAG_SIZE / 2;
                    offset.y = ui.position.top + SPATIAL_TAG_SIZE / 2;
                  },
                  stop(event) {
                    $scope.isDragging = false;

                    const percentOffsets = mouseEventOffsetsToPercentOffsets(
                      offset.x,
                      offset.y,
                    );

                    const offsetX = percentOffsets.percentOffsetX;
                    const offsetY = percentOffsets.percentOffsetY;

                    setSpatialTagPosition(offsetX, offsetY);
                    fadeSpatialTagCTABorder(true);
                  },
                });
              }, 0);
            }

            function setProducts(newProducts) {
              $scope.model.products = angular.copy(newProducts);
              $scope.model.unmodifiedProducts = angular.copy(newProducts);
              enableSpatialTagDragging();
            }

            $scope.$on('uia:products-changed', ($event, products) => {
              ProductTaggingService.setProducts(products);
              setProducts(products);
              $scope.model.loading = false;
            });

            $scope.$on('uia:invalid-products', ($event, errorState) => {
              setProducts(errorState.products);
            });

            $scope.$on('uia:products:disable-add-product', () => {
              $scope.model.disableAddingProducts = true;
            });

            $scope.$on('uia:products:enable-add-product', () => {
              $scope.model.disableAddingProducts = false;
            });

            $scope.$on('uia:products:cancelProductEditing', () => {
              // Undo any local changes to spatial tags that were reset by the editor's cancel button
              $scope.model.products = angular.copy(
                $scope.model.unmodifiedProducts,
              );
            });

            $scope.$on(
              'uia:products:setEditorSpatialTag',
              ($event, newSpatialTag) => {
                $scope.model.editorSpatialTag = newSpatialTag;
                enableSpatialTagDragging();
              },
            );

            $scope.$on('uia:products:setSpatialTagPosition', ($event, tag) => {
              if (tag.offsets) {
                setSpatialTagPosition(
                  tag.offsets.offsetX,
                  tag.offsets.offsetY,
                  tag.isNewTag,
                );
              }
            });

            $scope.$on(
              'uia:products:addSpatialTagInfo',
              ($event, product, isNewProduct) => {
                product.spatialTag = $scope.model.editorSpatialTag;
                product.position = $scope.model.position;

                $rootScope.$broadcast(
                  'uia:products:spatialTagInfoAdded',
                  product,
                  isNewProduct,
                );
              },
            );

            $scope.$on('uia:products:setEditorState', ($event, editorState) => {
              $scope.model.editor = editorState;
            });

            $scope.$on(
              'uia:products:setAddingProduct',
              ($event, isAddingProduct) => {
                $scope.model.addingProduct = isAddingProduct;
              },
            );

            const recomputeContainerDimensions = function () {
              $scope.$applyAsync(() => {
                const spatialTagContainer = angular.element(
                  '.spatial-tag-container:not(.ng-hide)',
                );
                containerWidth = spatialTagContainer.width();
                containerHeight = spatialTagContainer.height();
              });
            };

            const getSpatialTagOffsetString = function (point) {
              return `calc((${point * 100}%) - (${SPATIAL_TAG_SIZE / 2}px))`;
            };

            $scope.model.getSpatialTagXOffsetString = function (spatialTag) {
              if (!spatialTag) {
                return 0;
              }
              return getSpatialTagOffsetString(spatialTag.x);
            };

            $scope.model.getSpatialTagYOffsetString = function (spatialTag) {
              if (!spatialTag) {
                return 0;
              }
              const yOffset = spatialTag.y;
              return getSpatialTagOffsetString(yOffset);
            };

            $scope.model.activateTagEdit = (index, initialMouseDown) => {
              const dontOpenEditPanel = $scope.isDragging || initialMouseDown;
              $rootScope.$broadcast(
                'uia:edit-product-from-overlay',
                index,
                dontOpenEditPanel,
              );
            };

            $scope.model.getIndexFromTagLabel = (tag) => {
              if (tag) {
                return parseInt(tag.label, 10) - 1;
              } else {
                return -1;
              }
            };

            $scope.model.removeSpatialTag = function () {
              const productToRemoveTagFrom =
                $scope.model.products[$scope.model.editor.productIndex];
              if (productToRemoveTagFrom) {
                // Not the case when removing a tag from a newly added product
                productToRemoveTagFrom.spatialTag = null;
              }

              $scope.model.editorSpatialTag = null;
              $scope.model.position = null;
              $rootScope.$broadcast('uia:products:removeSpatialTag');
            };

            // When the user tags a product in the image or elects to add a product without a tag, we set a flag.
            $scope.model.beginAddProductFlow = function ($event) {
              if (eventIsWithinSpatialTagContainer($event)) {
                const mouseEventOffsetX =
                  $event.offsetX || $event.originalEvent.layerX;
                const mouseEventOffsetY =
                  $event.offsetY || $event.originalEvent.layerY;
                const percentOffsets = mouseEventOffsetsToPercentOffsets(
                  mouseEventOffsetX,
                  mouseEventOffsetY,
                );
                const offsetX = percentOffsets.percentOffsetX;
                const offsetY = percentOffsets.percentOffsetY;

                // If we're adding a spatial tag to an existing product inside the editor view, just set
                // the spatial tag position.
                // Otherwise, we're in the main productize view, adding a new product. Broadcast it.
                if ($scope.model.editor.enabled) {
                  setSpatialTagPosition(offsetX, offsetY, true);
                } else {
                  beginAddProductFlowInProductizePanel(offsetX, offsetY);
                }
              }
            };

            $scope.model.cancelNewSpatialTag = function () {
              $rootScope.$broadcast('uia:products:cancelNewSpatialTag');
            };

            $scope.$on('$destroy', () => {
              $window.removeEventListener(
                'resize',
                recomputeContainerDimensions,
              );
            });

            $scope.model.suggestionBoxClicked = function (
              boxId,
              coordinates,
              event,
            ) {
              const mouseEventOffsetX =
                event.offsetX || event.originalEvent.layerX;
              const mouseEventOffsetY =
                event.offsetY || event.originalEvent.layerY;

              const offsetXFromWidth =
                coordinates.x *
                angular.element('.spatial-tag-container:not(.ng-hide)').width();
              const offsetYFromHeight =
                coordinates.y *
                angular
                  .element('.spatial-tag-container:not(.ng-hide)')
                  .height();

              const percentOffsets = mouseEventOffsetsToPercentOffsets(
                mouseEventOffsetX + offsetXFromWidth,
                mouseEventOffsetY + offsetYFromHeight,
              );
              beginAddProductFlowInProductizePanel(
                percentOffsets.percentOffsetX,
                percentOffsets.percentOffsetY,
                boxId,
              );
            };

            UniversalImageActionsService.getItemMetadataAsync().then(() => {
              setProducts(ProductTaggingService.getProducts());
              $window.addEventListener('resize', recomputeContainerDimensions);
            });

            $scope.model.loading = false;
          },
        ],
      };
    },
  ]);
})();
